import { Platform, StyleSheet } from 'react-native';
import { theme } from '../../../theme';
import { makeBoxShadow } from '../../shared/helpers/shadowStyles.helper';

const CARD_BG = '#242B3D';
const INPUT_BG = '#161C2A';
const BORDER = '#2E3650';
const PURPLE = '#8B5CF6';
const PURPLE_DIM = '#6D3FD0';
/** Auth shell background (sign-in, reset password, SignInFlowSafeArea). */
export const SIGN_IN_OUTER_BACKGROUND = '#1E2330';

const OUTER_BG = SIGN_IN_OUTER_BACKGROUND;

const webAuthViewportFill =
  Platform.OS === 'web'
    ? ({
        minHeight: 'var(--app-dvh, 100dvh)' as never,
        height: 'var(--app-dvh, 100dvh)' as never,
      } as const)
    : ({} as const);

export const signInStyles = StyleSheet.create({
  /**
   * Outer shell for SafeAreaView on sign-in / reset-password flows.
   * Must match `screenRoot` background: without this, Android draws the parent
   * (e.g. root #000) in the top inset while the form uses OUTER_BG.
   */
  flowSafeRoot: {
    flex: 1,
    backgroundColor: OUTER_BG,
    ...webAuthViewportFill,
  },
  /** Fills SignInFlowSafeArea; background for auth flows */
  screenRoot: {
    flex: 1,
    backgroundColor: OUTER_BG,
    ...webAuthViewportFill,
  },
  scrollFill: {
    flex: 1,
  },
  card: {
    width: '100%',
    backgroundColor: CARD_BG,
    borderTopLeftRadius: 32,
    borderTopRightRadius: 32,
    paddingTop: 12,
    paddingHorizontal: 22,
    paddingBottom: 28,
    zIndex: 1,
    ...(Platform.OS === 'android' && { elevation: 12 }),
    ...(Platform.OS !== 'android' &&
      makeBoxShadow({
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -4 },
        shadowOpacity: 0.35,
        shadowRadius: 20,
      })),
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: OUTER_BG,
  },
  loadingText: {
    color: theme.white,
    marginTop: 16,
    fontSize: 16,
  },
  activityIndicator: {
    marginTop: 24,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: theme.white,
    textAlign: 'center',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: theme.gray[500],
    textAlign: 'center',
    marginBottom: 18,
    fontWeight: '400',
  },
  infoBanner: {
    backgroundColor: INPUT_BG,
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 14,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: BORDER,
  },
  infoBannerText: {
    fontSize: 13,
    color: theme.gray[500],
    textAlign: 'center',
    lineHeight: 18,
  },
  inputContainer: {
    width: '100%',
    marginBottom: 12,
  },
  input: {
    width: '100%',
    backgroundColor: INPUT_BG,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: BORDER,
    paddingVertical: 12,
    paddingHorizontal: 16,
    color: theme.white,
    fontSize: 15,
    minHeight: 48,
  },
  inputError: {
    borderColor: theme.error,
  },
  errorContainer: {
    marginTop: 4,
  },
  errorText: {
    color: theme.error,
    fontSize: 12,
    marginTop: 2,
  },
  primaryButton: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 14,
    minHeight: 48,
    backgroundColor: PURPLE,
    ...(Platform.OS === 'android' && { elevation: 6 }),
    ...(Platform.OS !== 'android' &&
      makeBoxShadow({
        shadowColor: PURPLE,
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.45,
        shadowRadius: 12,
      })),
  },
  primaryButtonPressed: {
    backgroundColor: PURPLE_DIM,
  },
  primaryButtonText: {
    color: theme.white,
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 0.3,
  },
  verificationHint: {
    fontSize: 14,
    color: theme.gray[500],
    marginBottom: 20,
    textAlign: 'center',
    paddingHorizontal: 20,
  },
  // Legacy aliases — shared by VerificationForm and reset-password
  button: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 14,
    minHeight: 52,
  },
  buttonText: {
    color: theme.white,
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 0.3,
  },
  linkButton: {
    paddingVertical: 6,
    alignItems: 'center',
  },
  linkButtonPressed: {
    opacity: 0.6,
  },
  linkText: {
    color: theme.gray[400],
    fontSize: 13,
  },
  primaryButtonHover: {
    backgroundColor: '#7C3AED',
  },
});
